import React, { useEffect, useRef, useState } from "react"
import axios, { AxiosProgressEvent } from "axios"
import { XCircleIcon } from "@heroicons/react/20/solid"

interface ImageUploaderProps {
  onUploadComplete: (urls: string[]) => void
  preExistingImages?: string[]
  showPreviews?: boolean
  buttonLabel?: string
}

const MAX_SIZE_MB = 50
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

type ImageState = {
  file: File
  url: string
  progress: number
  isUploading: boolean
  error: string | null
}

const uploadToSignedUrl = async (
  presignedUrl: string,
  file: File,
  onProgress: (p: number) => void,
  signal?: AbortSignal,
) => {
  return axios
    .put(presignedUrl, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: (e: AxiosProgressEvent) => {
        const loaded = e?.loaded ?? 0
        const total = e?.total ?? 0
        if (total > 0) {
          onProgress(loaded / total)
        }
      },
      signal,
    })
    .then(() => undefined)
    .catch((err) => {
      if (axios.isAxiosError(err)) {
        if (err.code === "ERR_CANCELED") {
          throw new Error("Upload aborted")
        }
        if (err.response) {
          throw new Error(`Upload failed with status ${err.response.status}`)
        }
        throw new Error("Network error during upload")
      }
      throw new Error(err?.message || "Network error during upload")
    })
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadComplete,
  preExistingImages,
  showPreviews = false,
  buttonLabel = "Resim Seç",
}) => {
  const [images, setImages] = useState<ImageState[]>(() => {
    return (
      preExistingImages?.map((url) => ({
        file: new File([], url.substring(url.lastIndexOf("/") + 1)),
        url: url,
        progress: 100,
        isUploading: false,
        error: null,
      })) || []
    )
  })
  const abortControllers = useRef<(AbortController | null)[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef<ImageState[]>(images)

  useEffect(() => {
    imagesRef.current = images
  }, [images])

  const uploadImage = async (file: File, index: number) => {
    abortControllers.current[index] = new AbortController()
    setImages((prev) =>
      prev.map((v, i) => (i === index ? { ...v, isUploading: true, error: null, progress: 0 } : v)),
    )
    try {
      const presignRes = await fetch(
        `${process.env.REACT_APP_AUTH_API_URL || "https://api.teamfluencer.co"}/upload/new`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ contentType: file.type }),
        },
      )
      if (!presignRes.ok) {
        throw new Error(`Presign request failed: ${await presignRes.text()}`)
      }
      const presignJson = await presignRes.json()
      const presignedUrl: string = presignJson.url
      const key: string = presignJson.key

      await uploadToSignedUrl(
        presignedUrl,
        file,
        (p) => {
          setImages((prev) =>
            prev.map((v, i) => (i === index ? { ...v, progress: Math.round(p * 100) } : v)),
          )
        },
        abortControllers.current[index]?.signal,
      )

      const finalUrl = presignedUrl.split(key)[0] + key

      setImages((prev) => {
        const updatedImages = prev.map((v, i) =>
          i === index ? { ...v, url: finalUrl, isUploading: false, progress: 100 } : v,
        )
        setTimeout(() => {
          onUploadComplete(updatedImages.filter((v) => v.url).map((v) => v.url))
        }, 500)
        return updatedImages
      })
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error("Image upload error:", err)
      const errorMessage =
        err instanceof Error ? err.message : "Resim yükleme sırasında hata oluştu."
      setImages((prev) =>
        prev.map((v, _i) => ({
          ...v,
          error: errorMessage,
          isUploading: false,
        })),
      )
      return
    }
  }

  const buildImageState = (file: File): ImageState => {
    if (file.size > MAX_SIZE_BYTES) {
      return {
        file,
        url: "",
        progress: 0,
        isUploading: false,
        error: `Dosya boyutu çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
      }
    }
    return { file, url: "", progress: 0, isUploading: false, error: null }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith("image/"))
    if (!files.length) {
      return
    }
    if (inputRef.current) {
      inputRef.current.value = ""
    }
    const newImages = files.map(buildImageState)
    const initialImagesLength = imagesRef.current.length

    setImages((prevImages) => [...prevImages, ...newImages])

    newImages.forEach((image, idx) => {
      if (!image.error) {
        uploadImage(image.file, initialImagesLength + idx)
      }
    })
  }

  const handleDelete = (index: number) => {
    if (abortControllers.current[index]) {
      abortControllers.current[index]?.abort()
      abortControllers.current[index] = null
    }
    const updated = images.filter((_, i) => i !== index)
    setImages(updated)
    onUploadComplete(updated.filter((v) => v.url).map((v) => v.url))
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="bg-[#3B82F6] text-white rounded-md h-12 min-w-[110px] px-5 font-poppins-medium hover:bg-blue-600 transition flex items-center justify-center whitespace-nowrap"
      >
        {buttonLabel}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      {showPreviews && (
        <div className="flex flex-wrap mt-4 gap-4 w-full">
          {images.map((image, index) => (
            <div
              key={`${image.url}-${index}`}
              className="relative w-full max-w-xl aspect-video bg-black rounded-2xl shadow-lg flex items-center justify-center mb-6"
              style={{ minHeight: 220 }}
            >
              {image.error ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-red-500 text-sm mt-2">{image.error}</span>
                </div>
              ) : image.isUploading ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <div className="w-3/4 bg-gray-300 rounded h-4 mt-2">
                    <div
                      className="bg-blue-500 h-4 rounded"
                      style={{ width: `${image.progress}%`, transition: "width 0.2s" }}
                    ></div>
                  </div>
                  <span className="block text-center mt-2 text-base font-semibold text-white">
                    {image.progress}%
                  </span>
                </div>
              ) : image.url ? (
                <img
                  src={image.url}
                  alt="uploaded"
                  className="w-full h-full object-cover rounded-2xl"
                  style={{ minHeight: 220, background: "#000" }}
                />
              ) : null}
              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="absolute top-2 right-2 bg-black bg-opacity-60 rounded-full p-2 hover:bg-opacity-90 transition"
                style={{ zIndex: 10 }}
              >
                <XCircleIcon className="w-8 h-8 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUploader
